import { Coupon } from '../preview-components/Coupon'
import { Header } from '../preview-components/Header'
import { ItemCard } from '../preview-components/ItemCard'
import { CartBanner } from '../preview-components/CartBanner'
import { Milestone } from '../preview-components/Milestone'
import { Upsell } from '../preview-components/Upsell'
import CartNotes from '../preview-components/Notes'
import { CartFooter } from '../preview-components/CartFooter'
import { useCartContext } from 'src/lib/contexts/CartContext'
import { CART_SCROLLABLE_ELEMENTS_TYPE } from './config'
import { useCartLayoutContext } from 'src/lib/contexts/CartLayoutContext'
import { CartFeatureHOC } from './CartFeatureHOC'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { useQuickAddOnPreviewTable } from 'src/lib/hooks/useQuickAddOnPreviewTable'
import { useCheckoutUI } from 'src/lib/contexts/CheckoutUIContext'

export const CartPreview = () => {
    const {
        actions: { setSelectedElement }
    } = useCartLayoutContext()
    const {
        getters: { cartUIData, cartBannerUIConfigData, cartBottomBannerUIConfigData }
    } = useCartContext()

    const {
        getters: { merchant }
    } = useMerchant()

    const quickAddOnTable = useQuickAddOnPreviewTable()
    const {
        getters: { checkoutUI }
    } = useCheckoutUI()

    const merchantPrimaryColor = checkoutUI?.colorPalette?.primaryColor

    return (
        <div className='relative flex flex-col h-full' onClick={() => setSelectedElement(null)}>
            <CartFeatureHOC element='HEADER' hideBorder>
                <Header headerConfig={cartUIData.header} enableShadow={!cartUIData.cartBannerUIConfigData.enabled} />
            </CartFeatureHOC>
            <CartFeatureHOC element='TOP_BANNER' hideBorder>
                <CartBanner
                    bannerPosition='HEADER'
                    cartBannerUIConfigData={cartBannerUIConfigData}
                    cartBottomBannerUIConfigData={cartBottomBannerUIConfigData}
                />
            </CartFeatureHOC>
            <CartScrollableElements />
            <CartFooter
                footerConfig={cartUIData.footer}
                addonConfig={cartUIData.addonConfig}
                primaryCTAConfig={cartUIData.primaryCTAConfig}
                merchantPrimaryColor={merchantPrimaryColor}
                quickAddOnTable={quickAddOnTable}
                cartBannerUIConfigData={cartBannerUIConfigData}
                cartBottomBannerUIConfigData={cartBottomBannerUIConfigData}
                enableShadow={!cartUIData.cartBottomBannerUIConfigData.enabled}
                showUpiIconsOnCartButton={cartUIData.showUpiIconsOnCartButton}
            />
        </div>
    )
}

// All scrollable element keys in default order
const SCROLLABLE_ELEMENT_KEYS: CART_SCROLLABLE_ELEMENTS_TYPE[] = ['ORDER_SUMMARY', 'DISCOUNTS', 'UPSELL', 'NOTES']

export const CartScrollableElements = () => {
    const {
        getters: { cartUIData },
        state: { progressBar }
    } = useCartContext()

    const {
        getters: { customizations, milestones, isCountBased }
    } = useCartContext()

    const {
        getters: { cartItems },
        actions: { updateCartItem }
    } = useCartLayoutContext()

    const {
        getters: { merchant }
    } = useMerchant()

    const quickAddOnTable = useQuickAddOnPreviewTable()
    const {
        getters: { checkoutUI }
    } = useCheckoutUI()

    const merchantPrimaryColor = checkoutUI?.colorPalette?.primaryColor
    const primaryButtonColor = cartUIData?.primaryCTAConfig?.backgroundColor ?? merchantPrimaryColor

    const isDiscountsVisible =
        cartUIData.couponVisibilityConfig.find(config => {
            return config.type === 'CART' && config.areCouponsVisible
        })?.areCouponsVisible ?? false
    const isNotesVisible = merchant?.attributes?.cartNote.enabled ?? false

    const hiddenElements: CART_SCROLLABLE_ELEMENTS_TYPE[] = [
        ...(!isDiscountsVisible ? ['DISCOUNTS' as const] : []),
        ...(!cartUIData.upsell.isEnabled ? ['UPSELL' as const] : []),
        ...(!isNotesVisible ? ['NOTES' as const] : [])
    ]

    const visibleElements = SCROLLABLE_ELEMENT_KEYS.filter(key => !hiddenElements.includes(key))

    // Get element order from context, fallback to default order if not set
    const elementOrder = cartUIData.elementOrder || visibleElements

    const renderScrollableElement = (elementType: CART_SCROLLABLE_ELEMENTS_TYPE) => {
        if (!visibleElements.includes(elementType)) return null

        switch (elementType) {
            case 'ORDER_SUMMARY':
                return (
                    <ItemCard
                        orderSummaryConfig={cartUIData.orderSummaryConfig}
                        quickAddOnTable={quickAddOnTable}
                        cartItems={cartItems}
                        updateCartItem={updateCartItem}
                        wishlistConfig={{
                            enabled: merchant?.wishlist?.config?.cart?.enabled ?? false,
                            primaryColor: primaryButtonColor ?? merchantPrimaryColor ?? ''
                        }}
                    />
                )
            case 'DISCOUNTS':
                return (
                    <Coupon
                        discountConfig={cartUIData.discountConfig}
                        merchantPrimaryColor={merchantPrimaryColor}
                        couponVisibilityStateConfig={cartUIData.couponVisibilityConfig.find(c => c.type === 'CART')}
                    />
                )
            case 'UPSELL':
                return (
                    <Upsell
                        upsellConfig={cartUIData.upsell}
                        wishlistConfig={{
                            enabled: merchant?.wishlist?.config?.cart?.enabled ?? false,
                            primaryColor: primaryButtonColor ?? merchantPrimaryColor ?? ''
                        }}
                    />
                )
            case 'NOTES':
                return (
                    <CartNotes
                        notesConfig={{
                            ...cartUIData.notesConfig,
                            buttonColor: primaryButtonColor ?? '',
                            buttonTextColor: cartUIData.primaryCTAConfig.textColor ?? ''
                        }}
                    />
                )
            default:
                return null
        }
    }

    return (
        <div
            className='flex-1 overflow-y-auto scrollbar-hide'
            style={{
                backgroundColor: cartUIData.page.backgroundColor
            }}
        >
            <CartFeatureHOC element='MILESTONE'>
                <Milestone
                    cartItems={cartItems}
                    progressBar={progressBar}
                    customizations={customizations}
                    milestones={milestones}
                    isCountBased={isCountBased}
                />
            </CartFeatureHOC>
            <div className='flex flex-col gap-3 px-3 pt-3 pb-5'>
                {elementOrder.map(elementType => {
                    const element = renderScrollableElement(elementType as CART_SCROLLABLE_ELEMENTS_TYPE)
                    return element ? (
                        <CartFeatureHOC key={elementType} element={elementType as CART_SCROLLABLE_ELEMENTS_TYPE}>
                            {element}
                        </CartFeatureHOC>
                    ) : null
                })}
            </div>
        </div>
    )
}
